import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Button, Label } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  createFormMapping,
  updateFormMapping,
} from "../../../../store/slices/formBuilder";
import { getAllPrograms } from "../../../../store/slices/programsSlice";
import Swal from "sweetalert2";

export const findOptionSelect = (options, slug) =>
  options?.find((o) => o?.value === slug) ?? null;

const initialFields = {
  level: null,
  program_slug: null,
  service_slug: null,
  sub_service_slug: null,
  category_slug: null,
  sub_category_slug: null,
};

const LEVEL_OPTIONS = [
  { value: 1, label: "Program" },
  { value: 2, label: "Service" },
  { value: 3, label: "SubService" },
  { value: 4, label: "Category" },
  { value: 5, label: "SubCategory" },
];

const selectStyles = {
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? "#82aaff" : provided.borderColor,
    boxShadow: "none",
    "&:hover": { borderColor: "#82aaff" },
    minHeight: 42,
  }),
};

export default function CreateFormMappingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { formId, mappingId } = useParams();

  // Determine mode from URL path segment
  const pathMode = location.pathname.includes("/edit/")
    ? "edit"
    : location.pathname.includes("/view/")
    ? "view"
    : "create";

  const formData = location.state?.formData || {};
  const mappingData = location.state?.mappingData || null;

  const dispatch = useDispatch();
  const [allPrograms, setAllPrograms] = useState([]);
  const [formFields, setFormFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isCreate = pathMode === "create";
  const isEdit = pathMode === "edit";
  const isView = pathMode === "view";

  const titleByMode = isCreate
    ? "Create Mapping"
    : isEdit
    ? "Edit Mapping"
    : "View Mapping";

  // Fetch programs on mount
  useEffect(() => {
    const fetchPrograms = async () => {
      const result = await dispatch(
        getAllPrograms({
          pagination: false,
          getServiceData: true,
          getSubServiceData: true,
          getFilteredData: true,
        })
      );
      const formatData = result?.payload?.map((program) => ({
        ...program,
        label: program?.program_name,
        value: program?.slug_name,
      }));
      if (Array.isArray(result.payload)) {
        setAllPrograms(formatData);
      }
    };
    fetchPrograms();
  }, []);

  // Hydrate form fields when editing or viewing
  useEffect(() => {
    if (mappingData && (isEdit || isView)) {
      setFormFields({
        ID: mappingData.ID,
        level: mappingData?.level ?? null,
        program_slug: mappingData?.program_slug ?? null,
        service_slug: mappingData?.service_slug ?? null,
        sub_service_slug: mappingData?.sub_service_slug ?? null,
        category_slug: mappingData?.category_slug ?? null,
        sub_category_slug: mappingData?.sub_category_slug ?? null,
        form_id: formId || mappingData?.form_id,
      });
    } else if (isCreate) {
      setFormFields(initialFields);
    }
  }, []);

  /** Cascading filters */
  const filteredServices = useMemo(() => {
    if (!formFields.program_slug) return [];
    return (
      allPrograms
        .find((s) => s?.slug_name === formFields.program_slug)
        ?.services?.map((s) => ({
          ...s,
          label: s?.service_name,
          value: s?.slug_name,
        })) || []
    );
  }, [allPrograms, formFields.program_slug]);

  const filteredSubServices = useMemo(() => {
    if (!formFields.service_slug) return [];
    return (
      filteredServices
        ?.find((s) => s?.slug_name === formFields?.service_slug)
        ?.sub_services?.map((s) => ({
          ...s,
          label: s?.sub_service_name,
          value: s?.slug_name,
        })) || []
    );
  }, [filteredServices, formFields.service_slug]);

  const filteredCategories = useMemo(() => {
    if (!formFields?.sub_service_slug) return [];
    return (
      filteredSubServices
        ?.find((s) => s?.slug_name === formFields?.sub_service_slug)
        ?.categories?.map((s) => ({
          ...s,
          label: s?.category_name,
          value: s?.slug_name,
        })) || []
    );
  }, [filteredSubServices, formFields?.sub_service_slug]);

  const filteredSubCategories = useMemo(() => {
    if (!formFields.category_slug) return [];
    return (
      filteredCategories
        ?.find((s) => s?.slug_name === formFields?.category_slug)
        ?.sub_categories?.map((s) => ({
          ...s,
          label: s?.sub_category_name,
          value: s?.slug_name,
        })) || []
    );
  }, [filteredCategories, formFields.category_slug]);

  const handleSelect = (option, key) => {
    setFormFields((prev) => {
      const next = { ...prev, [key]: option?.value ?? null };
      if (key === "program_slug") {
        next.service_slug = null;
        next.sub_service_slug = null;
      }
      if (key === "service_slug") {
        next.sub_service_slug = null;
      }
      if (key === "category_slug") {
        next.sub_category_slug = null;
      }
      return next;
    });
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const newErr = {};
    if (!formFields.level) newErr.level = "Level is required.";
    if (!formFields.program_slug) newErr.program_slug = "Program is required.";
    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  const buildPayload = () => ({
    level: formFields.level,
    program_slug: formFields.program_slug,
    service_slug: formFields.service_slug,
    sub_service_slug: formFields.sub_service_slug,
    category_slug: formFields.category_slug,
    sub_category_slug: formFields.sub_category_slug,
    form_id: formId,
    ...(mappingData?.ID && { ID: mappingData.ID }),
  });

  const handleSubmit = async () => {
    if (isView) return;
    if (!validate()) return;

    const payload = buildPayload();
    try {
      setSubmitting(true);
      if (isCreate) {
        await dispatch(createFormMapping(payload)).unwrap();
        await Swal.fire({
          icon: "success",
          title: "Mapping created",
          text: "Your form mapping has been saved successfully.",
          timer: 1800,
          timerProgressBar: true,
        });
      } else if (isEdit) {
        await dispatch(updateFormMapping(payload)).unwrap();
        await Swal.fire({
          icon: "success",
          title: "Mapping updated",
          text: "Your form mapping has been updated successfully.",
          timer: 1800,
          timerProgressBar: true,
        });
      }
      navigate(-1);
    } catch (err) {
      const message =
        err?.message ||
        err?.error ||
        err?.data?.message ||
        "Something went wrong. Please try again.";
      await Swal.fire({
        icon: "error",
        title: isCreate ? "Failed to create" : "Failed to update",
        text: message,
        confirmButtonText: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="container-fluid py-3"
      style={{ display: "flex", flexDirection: "column" ,backgroundColor: "#fff", minHeight: "80vh"}}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0.75rem 1.5rem",
          borderBottom: "1px solid #dee2e6",
          flexShrink: 0,
        }}
      >
        <div>
          <h5 className="mb-0">{titleByMode}</h5>
          {formData?.name && (
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Form: <strong>{formData.name}</strong>
            </Typography>
          )}
        </div>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => navigate(-1)}
        >
          &times; Close
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "1.5rem" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "1.25rem",
            maxWidth: 800,
          }}
        >
          {/* Level */}
          <div>
            <Label htmlFor="level">Level</Label>
            <Select
              id="level"
              options={LEVEL_OPTIONS}
              styles={selectStyles}
              isDisabled={isView}
              value={LEVEL_OPTIONS.find((o) => o.value === formFields.level) ?? null}
              onChange={(opt) => {
                setFormFields((prev) => ({
                  ...prev,
                  level: opt?.value ?? null,
                  ...(opt?.value < 2 && { service_slug: null }),
                  ...(opt?.value < 3 && { sub_service_slug: null }),
                  ...(opt?.value < 4 && { category_slug: null }),
                  ...(opt?.value < 5 && { sub_category_slug: null }),
                }));
                setErrors((e) => ({ ...e, level: undefined }));
              }}
              placeholder="Select level"
            />
            {errors.level && (
              <Typography variant="caption" color="error">
                {errors.level}
              </Typography>
            )}
          </div>

          {/* Program */}
          <div>
            <Label htmlFor="Program">Program</Label>
            <Select
              id="Program"
              options={allPrograms}
              styles={selectStyles}
              isDisabled={isView}
              value={findOptionSelect(allPrograms, formFields.program_slug)}
              onChange={(opt) => handleSelect(opt, "program_slug")}
              placeholder="Select a program"
            />
            {errors.program_slug && (
              <Typography variant="caption" color="error">
                {errors.program_slug}
              </Typography>
            )}
          </div>

          {/* Service */}
          {formFields.level >= 2 && (
          <div>
            <Label htmlFor="Service">Service</Label>
            <Select
              id="Service"
              options={filteredServices}
              styles={selectStyles}
              isDisabled={isView || !formFields.program_slug}
              value={findOptionSelect(filteredServices, formFields.service_slug)}
              onChange={(opt) => handleSelect(opt, "service_slug")}
              placeholder={
                formFields.program_slug
                  ? "Select a service"
                  : "Select program first"
              }
            />
            {errors.service_slug && (
              <Typography variant="caption" color="error">
                {errors.service_slug}
              </Typography>
            )}
          </div>
          )}

          {/* Sub Service */}
          {formFields.level >= 3 && (
          <div>
            <Label htmlFor="sub_service_slug">Sub Service</Label>
            <Select
              id="sub_service_slug"
              options={filteredSubServices}
              styles={selectStyles}
              isDisabled={isView || !formFields.service_slug}
              value={findOptionSelect(
                filteredSubServices,
                formFields.sub_service_slug
              )}
              onChange={(opt) => handleSelect(opt, "sub_service_slug")}
              placeholder={
                formFields.service_slug
                  ? "Select a sub service"
                  : "Select service first"
              }
            />
            {errors.sub_service_slug && (
              <Typography variant="caption" color="error">
                {errors.sub_service_slug}
              </Typography>
            )}
          </div>
          )}

          {/* Category */}
          {formFields.level >= 4 && (
          <div>
            <Label htmlFor="category_slug">Category</Label>
            <Select
              id="category_slug"
              options={filteredCategories}
              styles={selectStyles}
              isDisabled={isView || !formFields.sub_service_slug}
              value={findOptionSelect(
                filteredCategories,
                formFields.category_slug
              )}
              onChange={(opt) => handleSelect(opt, "category_slug")}
              placeholder={
                formFields.sub_service_slug
                  ? "Select a category"
                  : "Select sub-service first"
              }
            />
            {errors.category_slug && (
              <Typography variant="caption" color="error">
                {errors.category_slug}
              </Typography>
            )}
          </div>
          )}

          {/* Sub Category */}
          {formFields.level >= 5 && (
          <div>
            <Label htmlFor="sub_category_slug">Sub Category</Label>
            <Select
              id="sub_category_slug"
              options={filteredSubCategories}
              styles={selectStyles}
              isDisabled={isView || !formFields.category_slug}
              value={findOptionSelect(
                filteredSubCategories,
                formFields.sub_category_slug
              )}
              onChange={(opt) => handleSelect(opt, "sub_category_slug")}
              placeholder={
                formFields.category_slug
                  ? "Select a sub category"
                  : "Select category first"
              }
            />
            {errors.sub_category_slug && (
              <Typography variant="caption" color="error">
                {errors.sub_category_slug}
              </Typography>
            )}
          </div>
          )}
        </Box>

        {errors.form && (
          <Typography sx={{ mt: 2 }} variant="body2" color="error">
            {errors.form}
          </Typography>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          padding: "0.75rem 1.5rem",
          borderTop: "1px solid #dee2e6",
          flexShrink: 0,
        }}
      >
        <Button color="danger" outline onClick={() => navigate(-1)} disabled={submitting}>
          Close
        </Button>
        {!isView && (
          <Button color="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? isCreate
                ? "Creating..."
                : "Saving..."
              : isCreate
              ? "Create Mapping"
              : "Save Changes"}
          </Button>
        )}
      </div>
    </div>
  );
}
